import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';
import { logger } from '@parrot-software/observability';

import { getPlacesUseCases } from '@onboarding/composition';
import type { PlacePredictionDto, PlaceAddressDetailsDto } from '@onboarding/application/dtos';

const onbLog = logger.forDomain('ONB');

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

/**
 * Hook for debouncing a string value.
 */
const useDebouncedValue = (value: string, delay: number): string => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

type UseAddressSearchOptions = {
  /** Country code for restricting search (default: 'mx') */
  country?: string;
};

/**
 * Hook for address autocomplete search.
 *
 * Uses React Query for predictions caching and a mutation for place selection.
 * The Google Maps SDK is loaded lazily on first search.
 *
 * @example
 * const { query, setQuery, predictions, selectPlace } = useAddressSearch();
 */
export const useAddressSearch = (options?: UseAddressSearchOptions) => {
  const { fetchPredictions, getPlaceDetails } = getPlacesUseCases();
  const country = options?.country ?? 'mx';

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);

  const predictionsQuery = useQuery<PlacePredictionDto[], Error>(
    ['onboarding', 'places', 'predictions', debouncedQuery, country],
    () => fetchPredictions.execute({ input: debouncedQuery, country }),
    {
      enabled: debouncedQuery.length >= MIN_QUERY_LENGTH,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      onError: (error) => {
        onbLog.warn('address_prediction_failed', {
          'error.kind': error.constructor?.name ?? 'UnknownError',
          'error.message': error.message,
        });
      },
    },
  );

  const { mutateAsync, isLoading: isLoadingDetails, data: selectedAddressData } = useMutation<
    PlaceAddressDetailsDto,
    Error,
    string
  >((placeId: string) => getPlaceDetails.execute(placeId), {
    onError: (error, placeId) => {
      onbLog.warn('address_details_failed', {
        'error.kind': error.constructor?.name ?? 'UnknownError',
        'error.message': error.message,
        'onb.place_id': placeId,
      });
    },
  });

  const selectPlace = useCallback(async (placeId: string) => mutateAsync(placeId), [mutateAsync]);

  const reset = useCallback(() => {
    setQuery('');
  }, []);

  return {
    /** Current search query */
    query,
    /** Update the search query */
    setQuery,
    /** List of autocomplete predictions */
    predictions: predictionsQuery.data ?? [],
    /** Whether predictions are loading */
    isLoadingPredictions: predictionsQuery.isLoading && debouncedQuery.length >= MIN_QUERY_LENGTH,
    /** Whether place details are loading */
    isLoadingDetails,
    /** Whether there are results to show */
    hasResults: (predictionsQuery.data?.length ?? 0) > 0,
    /** Select a place by placeId — returns parsed address details */
    selectPlace,
    /** The selected address details (or null) */
    selectedAddress: selectedAddressData ?? null,
    /** Reset query and state */
    reset,
  };
};
